import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const Emotion: React.FC<{ extended?: boolean }> = ({ extended = false }) => {
  const { t } = useTranslation();

  return (
    <section
      id="founders-vision"
      aria-label={t("emotion.eyebrow", "Vision de la fondatrice")}
      className="relative py-24 sm:py-32 lg:py-36 px-6 sm:px-8 lg:px-12 bg-[#F8F5EF] overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-center gap-5 mb-16 sm:mb-20 lg:mb-24 max-w-xl">
          <span className="font-oswald text-[11px] sm:text-xs uppercase tracking-[0.34em] text-[#86523d] whitespace-nowrap">
            {t("emotion.eyebrow", "Vision de la fondatrice")}
          </span>
          <span className="h-px flex-1 bg-stone-300/80" aria-hidden="true" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-16 xl:gap-24 items-center">

          {/* ================= LEFT COLUMN: EDITORIAL STATEMENT ================= */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="lg:col-span-7 lg:order-1 flex flex-col justify-center"
          >
            {extended && (
              <p className="mb-10 max-w-2xl font-oswald text-[1.35rem] font-normal leading-[1.35] tracking-[-0.02em] text-stone-900 sm:text-[1.6rem] lg:text-[1.85rem]">
                {t("emotion.practice")}
              </p>
            )}

            <blockquote className={`relative mb-12 ${extended ? "pl-8 sm:pl-10" : "pl-11 sm:pl-14"}`}>
              <span
                className={`absolute -top-1 left-0 select-none font-playfair font-bold leading-none text-[#a26850] ${
                  extended ? "text-5xl sm:text-6xl" : "text-6xl sm:text-7xl"
                }`}
                aria-hidden="true"
              >
                “
              </span>
              <p
                className={`relative whitespace-pre-line font-playfair text-stone-900 ${
                  extended
                    ? "text-[1.35rem] italic leading-[1.4] sm:text-[1.55rem] lg:text-[1.75rem]"
                    : "text-2xl font-normal leading-[1.28] tracking-[-0.025em] sm:text-3xl lg:text-[2.35rem] xl:text-[2.65rem]"
                }`}
              >
                {t(
                  "emotion.quote",
                  "Ma vision de l’immobilier va au-delà des biens. Il s’agit de créer un service pensé autour du style de vie, des attentes et des ambitions de chaque client."
                )}
              </p>
            </blockquote>

            {/* AUTHOR */}
            <div className="flex items-center gap-5 pt-7 border-t border-stone-300/80 max-w-xl">
              <span className="h-px w-10 bg-[#a26850]" aria-hidden="true" />
              <div>
                <span className="block font-oswald text-xs uppercase tracking-[0.25em] text-stone-900">
                  {t("emotion.name", "Dounia El Moutii")}
                </span>
                <span className="block font-playfair italic text-sm text-stone-500 mt-1">
                  {t("emotion.role", "Fondatrice & Conseillère principale")}
                </span>
              </div>
            </div>
          </motion.div>

          {/* ================= RIGHT COLUMN: PHOTO & OFFICIAL API CERTIFICATION ================= */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="lg:col-span-5 lg:order-2 relative flex justify-center lg:justify-end"
          >
            <div className="relative w-full max-w-[390px] lg:max-w-[420px] px-3 pt-3 pb-4">
              <div
                className="absolute left-0 top-0 right-7 bottom-7 border border-[#a26850]/35"
                aria-hidden="true"
              />
              {/* MAIN IMAGE CONTAINER */}
              <div className="relative w-full aspect-[4/5] overflow-hidden bg-stone-100 shadow-[0_18px_45px_rgba(58,43,34,0.12)]">
                <img
                  src="/img.jpeg" width={657} height={821}
                  alt={t("emotion.imageAlt", "Portrait de Dounia El Moutii, fondatrice de DEM Properties")}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  fetchPriority="low"
                  decoding="async"
                />
              </div>

              {/* OFFICIAL API CERTIFICATION */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true }}
                className="absolute bottom-7 right-6 z-20 w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#F8F5EF] p-1.5 shadow-[0_8px_24px_rgba(0,0,0,0.18)]"
                title={t("emotion.certificationAlt", "Certification officielle API Agente de la Propiedad Inmobiliaria")}
              >
                <img
                  src="/certif.webp" width={300} height={300}
                  alt={t("emotion.certificationAlt", "Certification API")}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  fetchPriority="low"
                  decoding="async"
                />
              </motion.div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Emotion;
